/**
 * Texto BASE del servidor sobre el que se escribió un respaldo local de nota.
 *
 * El respaldo (`utils/borradorNota.ts`) guarda lo que la aseadora escribió, pero
 * no desde qué versión del servidor partió. Sin eso, al reabrir las notas días
 * después no se puede saber si el texto del servidor cambió mientras tanto (p. ej.
 * el administrador editó la misma nota) y recuperar el respaldo pisaba ese cambio
 * sin aviso. Aquí se guarda ese texto base, con la misma clave del respaldo.
 *
 * localStorage puede fallar o venir vacío: todo va en try/catch y, sin base, la
 * recuperación se comporta de forma conservadora (ver `decidirRecuperacion`).
 */

const PREFIJO = "aseo.nota.base.v1:";

export function leerBase(claveRespaldo: string): string | null {
  try {
    const raw = localStorage.getItem(PREFIJO + claveRespaldo);
    if (raw === null) return null;
    const b = JSON.parse(raw) as { base?: unknown } | null;
    return typeof b?.base === "string" ? b.base : null;
  } catch {
    return null;
  }
}

export function guardarBase(claveRespaldo: string, base: string): void {
  try {
    localStorage.setItem(PREFIJO + claveRespaldo, JSON.stringify({ base }));
  } catch {
    /* sin base: la recuperación será conservadora */
  }
}

/** Guarda la base solo si el respaldo todavía no tiene una (la primera edición manda). */
export function guardarBaseSiFalta(claveRespaldo: string, base: string): void {
  if (leerBase(claveRespaldo) === null) guardarBase(claveRespaldo, base);
}

export function limpiarBase(claveRespaldo: string): void {
  try {
    localStorage.removeItem(PREFIJO + claveRespaldo);
  } catch {
    /* nada */
  }
}

export type DecisionRecuperacion = "recuperar" | "conflicto";

/**
 * Qué hacer con un respaldo local que difiere del texto actual del servidor:
 *  - el servidor sigue igual que cuando se escribió el respaldo (o está vacío, sin
 *    nada que perder) → "recuperar" y autoguardar;
 *  - el servidor cambió desde entonces, o no se sabe → "conflicto": se conserva el
 *    texto del servidor, se avisa y se deja elegir (nunca se pisa en silencio).
 */
export function decidirRecuperacion(
  textoServidor: string,
  baseDelRespaldo: string | null,
): DecisionRecuperacion {
  if (textoServidor === "") return "recuperar";
  if (baseDelRespaldo !== null && baseDelRespaldo === textoServidor) return "recuperar";
  return "conflicto";
}
